import { BIDDING_TYPES, biddingInstants, toApiCalendarDate } from "../../utils";
import { DELIVERY_TYPE } from "../../types";
import { serviceDetailPayload } from "./serviceDetail";

/**
 * The fields that are DERIVED IDENTICALLY in both order-launch payloads
 * (`buildOrderPayload` for the RFQ builder and `buildRequestOrderPayload` for
 * launch-from-PR). Extracting them here removes the copy-pasted field ladder
 * without merging the two wire contracts: each builder spreads this core and then
 * adds/overrides the fields that genuinely differ between the flows (quantity,
 * internalCode, initialPrice/minimumDecrement, supply dates, and the request-only
 * PR/edit fields). The per-builder characterization tests pin the full output, so
 * any drift from the pre-Carbon body still fails a test.
 */
export type OrderBodyCoreCtx = {
  selectedType: { value: string };
  selectedComplexType: { value: string };
  user:
    | { id?: string; email?: string; fullName?: string }
    | null
    | undefined;
};

export const buildOrderBodyCore = (
  mat: any,
  generalBidData: any,
  { selectedType, selectedComplexType, user }: OrderBodyCoreCtx
) => ({
  paymentMethod: generalBidData.paymentMethod,
  startDate: toApiCalendarDate(generalBidData.startDateValue),
  finishDate: toApiCalendarDate(generalBidData.endDateValue),
  ...biddingInstants(generalBidData),
  consultationStartDate: generalBidData.consultationStartValue
    ? toApiCalendarDate(generalBidData.consultationStartValue)
    : null,
  consultationEndDate: generalBidData.consultationEndValue
    ? toApiCalendarDate(generalBidData.consultationEndValue)
    : null,
  material: { id: mat.id },
  user_id: user?.id,
  email: user?.email,
  files: [...(generalBidData.files ?? []), ...((mat as any).files ?? [])],
  deliveryPlace: mat?.deliveryPlace ? { id: mat?.deliveryPlace?.id } : null,
  // Free-text place, only when no site is picked (SCRUM-55: where a service is performed).
  address: mat?.deliveryPlace?.id ? undefined : mat?.address || undefined,
  fullname: user?.fullName,
  brand: mat?.brand,
  purpose: mat?.purpose,
  name: generalBidData.name ?? mat?.name,
  fileUrl: mat?.fileUrl,
  measurementUnit: mat?.measurementUnit,
  incoTerm: generalBidData?.incoTerm || mat?.incoTerm,
  currency: mat?.coin || generalBidData?.currency,
  spot: mat?.spot === DELIVERY_TYPE.SPOT ? true : false,
  expireDate: generalBidData?.expireDate,
  buyerPicksUp: Boolean(generalBidData?.buyerPicksUp),
  type:
    selectedType.value === BIDDING_TYPES.INVERSE &&
    selectedComplexType.value === BIDDING_TYPES.JOINT
      ? BIDDING_TYPES.INVERSE_JOINT
      : selectedComplexType.value === BIDDING_TYPES.JOINT
        ? BIDDING_TYPES.JOINT
        : selectedType.value,
  comment: generalBidData.comment,
  suppliers: mat?.suppliers.filter((sup: any) => sup.id),
  paymentTerm: parseInt(generalBidData.paymentTerm),
  // SCRUM-112/117: what suppliers read about a service before quoting (nothing for goods). On a
  // joint quotation each service keeps its own name; on a single one it is the quotation's.
  ...serviceDetailPayload(mat, selectedComplexType.value !== BIDDING_TYPES.JOINT),
});
